import React, { useEffect, useState, useRef } from 'react';
import { Activity, Search, Bell, Radio, CheckCircle2, AlertTriangle } from 'lucide-react';
interface LayoutProps {
  children: React.ReactNode;
}
type MenuKey = 'bell' | null;
interface Notification {
  id: number;
  title: string;
  time: string;
  tone: 'critical' | 'warning' | 'info';
}
const NOTIFICATIONS: Notification[] = [{
  id: 1,
  title: 'Rate Watch sync mismatch (Δ 150)',
  time: '5m ago',
  tone: 'critical'
}, {
  id: 2,
  title: 'VA Cash Out variance trending up',
  time: '12m ago',
  tone: 'warning'
}, {
  id: 3,
  title: 'Global sync completed',
  time: '15m ago',
  tone: 'info'
}];
export function Layout({
  children
}: LayoutProps) {
  const [search, setSearch] = useState('');
  const [openMenu, setOpenMenu] = useState<MenuKey>(null);
  const [unread, setUnread] = useState(NOTIFICATIONS.length);
  const headerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [lastRefresh, setLastRefresh] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setLastRefresh(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpenMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const toggle = (key: MenuKey) => {
    setOpenMenu(cur => cur === key ? null : key);
    if (key === 'bell') setUnread(0);
  };
  return <div className="min-h-screen flex flex-col bg-slate-950 text-slate-200" data-id="element-219">
      <header ref={headerRef} className="h-16 border-b border-slate-800/60 bg-slate-900/50 backdrop-blur-md sticky top-0 z-50 px-6 flex items-center justify-between" data-id="element-220">
        <div className="flex items-center gap-6" data-id="element-221">
          <div className="flex items-center gap-2 text-indigo-400" data-id="element-222">
            <Activity className="w-6 h-6" data-id="element-223" />
            <span className="font-semibold text-lg tracking-tight text-white" data-id="element-224">
              Command Center
            </span>
          </div>

          <div className="hidden md:block h-6 w-px bg-slate-800" data-id="element-225"></div>

          <div className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium" data-id="element-226">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-227"></div>
            Production
          </div>

          <div className="hidden lg:flex items-center gap-2 px-2.5 py-1 rounded-full bg-slate-800/50 border border-slate-700/50 text-slate-400 text-xs font-medium" data-id="element-228">
            <Radio className="w-3 h-3 text-sky-400" data-id="element-229" />
            Live Sync Active
          </div>
        </div>

        <div className="flex items-center gap-4" data-id="element-230">
          <div className="relative group hidden md:block" data-id="element-231">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-indigo-400 transition-colors pointer-events-none" data-id="element-232" />
            <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search lists, leads, or criteria..." className="bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-16 py-1.5 text-sm focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 w-64 transition-all placeholder:text-slate-600" data-id="element-233" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none" data-id="element-234">
              <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-slate-800 rounded border border-slate-700" data-id="element-235">
                ⌘
              </kbd>
              <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-slate-800 rounded border border-slate-700" data-id="element-236">
                K
              </kbd>
            </div>
          </div>

          <div className="hidden md:block h-6 w-px bg-slate-800" data-id="element-237"></div>

          <div className="flex items-center gap-2" data-id="element-238">
            <div className="text-xs text-slate-500 text-right hidden lg:block" data-id="element-239">
              <div data-id="element-240">Last refresh</div>
              <div className="text-slate-300 tabular-nums" data-id="element-241">
                {lastRefresh.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
              </div>
            </div>

            {/* Bell */}
            <div className="relative" data-id="element-242">
              <button onClick={() => toggle('bell')} aria-label="Notifications" className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-243">
                <Bell className="w-5 h-5" data-id="element-244" />
                {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-rose-500 rounded-full text-[10px] font-bold text-white border-2 border-slate-900 flex items-center justify-center tabular-nums" data-id="element-245">
                    {unread}
                  </span>}
              </button>
              {openMenu === 'bell' && <div className="absolute right-0 top-full mt-2 w-80 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden" data-id="element-246">
                  <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between" data-id="element-247">
                    <h3 className="text-sm font-semibold text-white" data-id="element-248">
                      Notifications
                    </h3>
                    <button className="text-xs text-indigo-400 hover:text-indigo-300" data-id="element-249">
                      Mark all read
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-800/60" data-id="element-250">
                    {NOTIFICATIONS.map(n => <div key={n.id} className="px-4 py-3 hover:bg-slate-800/50 cursor-pointer" data-id="element-251">
                        <div className="flex items-start gap-2.5" data-id="element-252">
                          {n.tone === 'critical' ? <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" data-id="element-253" /> : n.tone === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-254" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" data-id="element-255" />}
                          <div className="min-w-0" data-id="element-256">
                            <p className="text-sm text-slate-200 leading-snug" data-id="element-257">
                              {n.title}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-258">
                              {n.time}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-x-hidden" data-id="element-259">{children}</main>
    </div>;
}